import numeral from 'numeral'
import { Charts } from '@ui'
import { options as dateOptions } from '@ui/selects/DateRangeSelect'
const format = (n: number) => numeral(n).format('0,0')
const pluralize = (str: string, n: number) => (n === 1 ? str : `${str}s`)

export const COLORS = [
  '128dee',
  '10b4df',
  '94e1ff',
  '26e8d4',
  '24c7a3',
  '8938eb',
  '8e6cef',
  '9c46d0',
  'c758d0',
  'e01e84',
]
export const STACKED_COLORS = ['128dee', '10b4df', '94e1ff', 'd7dde1']

export const getColor = (index: number) => COLORS[index % COLORS.length]

let colorMap: { [s: string]: any } = {}

export const getColorScale = (chartName: string, stacked: boolean) => {
  let colors = colorMap[chartName]
  if (colors) return colors

  colors = Charts.colorScale(stacked)
  colorMap = { ...colorMap, [chartName]: colors }
  return colors
}

interface IOpts {
  x: number
  y: number
}

const options: { [name: string]: any } = {
  /**
   * OCCUPANCY reports
   */
  SitesOccupancyReport: {
    'By Allocated, Assigned, and Unassigned': {
      x: ['Assigned', 'Allocated', 'Unassigned'],
      y: 'Location',
      xLabel: 'Count',
      filter: 'site',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        location: payload['Location'],
      }),
    },
    'By Capacity': {
      label: 'Location',
      value: 'Count',
      percent: 'Percent',
      filter: 'site',
      payloadToFilter: (payload: any) =>
        payload.label !== 'Other'
          ? {
              id: payload.id,
              location: payload.label,
            }
          : undefined,
    },
    'By Occupancy (%)': {
      x: 'Count',
      y: 'Location',
      xLabel: 'Occupancy (%)',
      unit: '%',
      filter: 'site',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        location: payload['Location'],
      }),
    },
  },

  BuildingsOccupancyReport: {
    'By Allocated, Assigned, and Unassigned': {
      x: ['Assigned', 'Allocated', 'Unassigned'],
      y: 'Building',
      xLabel: 'Count',
      filter: 'buildingId',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        name: payload['Building'],
        city: '',
      }),
    },
    'By Occupancy (%)': {
      x: 'Count',
      y: 'Building',
      xLabel: 'Occupancy (%)',
      unit: '%',
      filter: 'buildingId',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        name: payload['Building'],
        city: '',
      }),
    },
  },

  FutureOccupancyReport: {
    past: false,
    future: true,
    defaultDateFilter: dateOptions.future[1],
    'By Building': {
      x: ['Future Occupancy', 'Extra 5% Growth', 'Extra 10% Growth', 'Capacity'],
      y: 'Building',
      xLabel: 'Count',
      filter: 'buildingId',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        name: payload['Building'],
        city: '',
      }),
    },
  },

  /**
   * EMPLOYEES reports
   */

  HeadcountByTeamReport: {
    'By City': {
      x: 'Count',
      y: 'City',
    },
    'By Department': {
      x: 'Count',
      y: 'Department',
      filter: 'departmentId',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        name: payload['Department'],
        color: payload.color,
      }),
    },
  },

  NewHires: {
    past: true,
    future: true,
    defaultDateFilter: dateOptions.future[1],
    'By Location': {
      label: 'Site',
      value: 'Count',
      percent: 'Percent',
      filter: 'site',
      payloadToFilter: (payload: any) =>
        payload.label !== 'Other'
          ? {
              id: payload.id,
              location: payload.label,
            }
          : undefined,
    },
    'By Department': {
      y: 'Department',
      x: 'Count',
      xLabel: 'Employees',
      filter: 'departmentId',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        name: payload['Department'],
        color: payload.color,
      }),
    },
    'By Employment Type': {
      label: 'Employment Type',
      value: 'Count',
      percent: 'Percent',
      filter: 'employmentType',
      payloadToFilter: (payload: any) => (payload.label !== 'Other' ? payload.label : undefined),
    },
  },

  TerminatedEmployees: {
    past: true,
    future: false,
    defaultDateFilter: dateOptions.past[1],
    'By Location': {
      label: 'Site',
      value: 'Count',
      percent: 'Percent',
      filter: 'site',
      payloadToFilter: (payload: any) =>
        payload.label !== 'Other'
          ? {
              id: payload.id,
              location: payload.label,
            }
          : undefined,
    },
    'By Department': {
      y: 'Department',
      x: 'Count',
      xLabel: 'Employees',
      filter: 'departmentId',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        name: payload['Department'],
        color: payload.color,
      }),
    },
    'By Employment Type': {
      label: 'Employment Type',
      value: 'Count',
      percent: 'Percent',
      filter: 'employmentType',
      payloadToFilter: (payload: any) => (payload.label !== 'Other' ? payload.label : undefined),
    },
  },

  FutureTerminations: {
    past: false,
    future: true,
    defaultDateFilter: dateOptions.future[1],
    'By Location': {
      label: 'Site',
      value: 'Count',
      percent: 'Percent',
      filter: 'site',
      payloadToFilter: (payload: any) =>
        payload.label !== 'Other'
          ? {
              id: payload.id,
              location: payload.label,
            }
          : undefined,
    },
    'By Department': {
      y: 'Department',
      x: 'Count',
      xLabel: 'Employees',
      filter: 'departmentId',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        name: payload['Department'],
        color: payload.color,
      }),
    },
    'By Employment Type': {
      label: 'Employment Type',
      value: 'Count',
      percent: 'Percent',
      filter: 'employmentType',
      payloadToFilter: (payload: any) => (payload.label !== 'Other' ? payload.label : undefined),
    },
  },

  /**
   * SPACES reports
   */
  SpacesReport: {
    useFloorFilter: true,
    'By Space Type': {
      y: 'Space Type',
      x: 'Count',
      xLabel: 'Spaces',
      filter: 'spaceTypeId',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        displayName: payload['Space Type'],
      }),
    },
  },

  SpaceTypesReport: {
    useFloorFilter: true,
  },
  PinsReport: {
    useFloorFilter: true,
  },

  SpacesCostCenterReport: {
    useFloorFilter: true,
    'By Space Type': {
      y: 'Space Type',
      x: 'Count',
      xLabel: 'Spaces',
      filter: 'spaceTypeId',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        displayName: payload['Space Type'],
      }),
    },
  },

  MoveHistoryReport: {
    past: true,
    future: true,
    defaultDateFilter: dateOptions.future[1],
    'By Department': {
      x: 'Count',
      y: 'Department',
      xLabel: 'Moves',
      filter: 'departmentId',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        name: payload['Department'],
        color: payload.color,
      }),
    },
    'By Location': {
      label: 'Location',
      value: 'Count',
      percent: 'Percent',
      filter: 'site',
      payloadToFilter: (payload: any) =>
        payload.label !== 'Other'
          ? {
              id: payload.id,
              location: payload.label,
            }
          : undefined,
    },
  },

  SpaceUsageTypesReport: {
    useFloorFilter: true,
    'By Usage Types': {
      x: 'Count',
      y: 'Usage Type',
      xLabel: 'Spaces',
      filter: 'usageTypeId',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        displayName: payload['Usage Type'],
      }),
    },
  },

  SecondarySeatsReport: {
    'By City': {
      label: 'City',
      value: 'Count',
      percent: 'Percent',
    },
    'By Department': {
      x: 'Count',
      y: 'Department',
      xLabel: 'Secondary Seats',
      filter: 'departmentId',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        name: payload['Department'],
        color: payload.color,
      }),
    },
    'By Building': {
      x: 'Count',
      y: 'Building',
      xLabel: 'Secondary Seats',
      filter: 'buildingId',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        name: payload['Building'],
        city: '',
      }),
    },
  },

  /**
   * LEASE/COSTS reports
   */
  Sites: {
    'Annual Lease Cost per Location ($)': {
      label: 'Site',
      value: 'Value',
      percent: 'Percent',
      unit: '$',
      filter: 'site',
      payloadToFilter: (payload: any) =>
        payload.label !== 'Other'
          ? {
              id: payload.id,
              location: payload.label,
            }
          : undefined,
    },
    'Sqft per Location': {
      x: 'Value',
      y: 'Site',
      xLabel: 'Area (sqft)',
      unit: 'sqft',
      filter: 'site',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        location: payload['Site'],
      }),
    },
    'Seats per Location': {
      x: 'Value',
      y: 'Site',
      xLabel: 'Capacity',
      filter: 'site',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        location: payload['Site'],
      }),
    },
  },

  Buildings: {
    'Total Cost per Building ($)': {
      x: 'Value',
      y: 'Building',
      xLabel: 'Cost ($)',
      unit: '$',
      filter: 'buildingId',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        name: payload['Building'],
        city: '',
      }),
    },
    'Sqft per Building': {
      x: 'Value',
      y: 'Building',
      unit: 'sqft',
      xLabel: 'Area (sqft)',
      filter: 'buildingId',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        name: payload['Building'],
        city: '',
      }),
    },
  },
  Floors: {
    useFloorFilter: true,
  },

  /**
   * AGILE WORKPLACE reports
   */
  MeetingRoomBookingReport: {
    past: true,
    future: true,
    defaultDateFilter: dateOptions.future[1],
    'Meetings by Building': {
      x: 'Total number of meetings',
      y: 'Building',
      xLabel: '# of Meetings',
      filter: 'buildingId',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        name: payload['Building'],
        city: '',
      }),
    },
    'Meetings by Rooms': {
      x: 'Total number of meetings',
      y: 'Room',
      xLabel: '# of Meetings',
    },
    'Distribution of employees in meetings': {
      x: 'number of employees',
      y: 'number of meetings',
      xLabel: '# of Atendees',
      yLabel: '# of Meetings',
      tooltip: ({ x, y }: IOpts) =>
        `${y} ${pluralize('meeting', y)} with ${x} attending ${pluralize('employee', x)}`,
    },
    includeOldBuildings: true,
  },

  HotelDeskReport: {
    'Bookings By Department': {
      x: 'Total number of bookings',
      y: 'Department',
      xLabel: '# of Bookings',
      filter: 'departmentId',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        name: payload['Department'],
        color: payload.color,
      }),
    },
    'Bookings By Building': {
      x: 'Total number of bookings',
      y: 'Building',
      xLabel: '# of Bookings',
      filter: 'buildingId',
      payloadToFilter: (payload: any) => ({
        id: payload.id,
        name: payload['Building'],
        city: '',
      }),
    },
    'Distribution Of Bookings By Day Of The Week': {
      x: 'Day of the week',
      y: 'Number of bookings',
      xLabel: 'Day',
      yLabel: '# of Bookings',
      tooltip: ({ x, y }: IOpts) => `${y} ${pluralize('booking', y)} on ${x}`,
    },
    past: true,
    future: true,
    defaultDateFilter: dateOptions.past[1],
    includeOldBuildings: true,
  },
  HotelDeskBookingReport: {
    noStickyHeader: true,
    past: true,
    future: true,
    defaultDateFilter: dateOptions.past[1],
  },

  /**
   * SENSORS reports
   */
  OverallMeetingRoomUtilization: {
    useFloorFilter: true,
    past: true,
    future: false,
    defaultDateFilter: dateOptions.past[1],
    'Meeting room capacities': {
      unit: '',
      x: 'Capacity',
      y: 'Room Count',
      tooltip: ({ x, y }: IOpts) =>
        `${format(y)} meeting ${pluralize('room', y)} with capacity of ${x} ${
          x === 1 ? 'person' : 'people'
        }`,
    },
    table: {
      noStickyHeader: true,
      link: {
        column: 'Room Name',
        to: '/reports/SingleMeetingRoomUtilization',
      },
    },
    includeOldBuildings: true,
  },

  SingleMeetingRoomUtilization: {
    useFloorFilter: true,
    past: true,
    future: false,
    defaultDateFilter: dateOptions.past[1],
    Occupancy: {
      x: 'Day of the week',
      y: 'Occupancy',
      tooltip: ({ x, y }: IOpts) => `${y}% occupancy on ${x}`,
    },
    Attendees: {
      x: 'Number of attendees',
      y: 'Count',
      xLabel: '# of Attendees',
      yLabel: '# of Meetings',
      tooltip: ({ x, y }: IOpts) =>
        `${format(y)} ${pluralize('meeting', y)} with ${x} ${pluralize('attendee', x)} `,
    },
    table: {
      noStickyHeader: true,
    },
  },

  OverallSeatsUtilization: {
    past: true,
    future: false,
    defaultDateFilter: dateOptions.past[1],
    Occupancy: {
      x: 'Day of the week',
      y: 'Occupancy',
      yLabel: 'Occupancy (%)',
      unit: '%',
      tooltip: ({ x, y }: IOpts) => `${y}% occupancy on ${x}`,
    },
    'Space Usage Types': {
      y: 'Usage Type',
      x: 'Usage Type Count',
      xLabel: 'Count',
      filter: 'usageTypeId',
    },
    table: {
      noStickyHeader: true,
      link: {
        column: 'Space #',
        to: '/reports/SingleSeatUtilization',
      },
    },
    includeOldBuildings: true,
  },

  SingleSeatUtilization: {
    past: true,
    future: false,
    defaultDateFilter: dateOptions.past[1],
    Occupancy: {
      x: 'Day of the week',
      y: 'Occupancy',
      yLabel: 'Occupancy (%)',
      unit: '%',
      tooltip: ({ x, y }: IOpts) => `${y}% occupancy on ${x}`,
    },

    noStickyHeader: true,
  },

  /**
   * Badging reports
   */

  AttendancePerBuilding: {
    includeOldBuildings: true,
    past: true,
    future: true,
    defaultDateFilter: dateOptions.past[1],
  },

  AttendancePerDepartment: {
    includeOldBuildings: true,
    past: true,
    future: true,
    defaultDateFilter: dateOptions.past[1],
  },

  FinancialForecastByBuilding: {
    defaultFromYearFilter: new Date().getFullYear(),
    defaultToYearFilter: new Date().getFullYear() + 9,
    yearSelector: true,
    endYear: true,
    noStickyHeader: true,
    includeOldBuildings: true,
  },

  default: {
    past: true,
    future: true,
    defaultDateFilter: dateOptions.past[1],
    'Chart 1': {},
    'Chart 2': {},
    'Chart 3': {},
  },
}

export default options
